import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CARD_LAST_VISITS,
  CLIENT_NOTE_TEXT_MAX,
  PAYMENT_METHODS,
  REMAINDER_METHODS,
  billTotalMinor,
  lineDiscrepancies,
  localize,
  PROTOCOL_SECTIONS,
  protocolForClient,
  shortName,
  totalMinor,
  type GiftCertificateView,
  type PaymentMethod,
  type ClientCard,
  type ShopConfig,
  type ShopProduct,
  type ProductOffer,
  type RemainderMethod,
  type Slot,
  type QuestionnaireAvailability,
  type Service,
  type StaffQuestionnaireView,
  type Staff,
  type StaffVisit,
} from '@360face/domain';
import { ApiError, type HubApi } from '../api/client';
import { serviceArt } from '../v2/kiosk/art';
import { BrandSplash } from '../splash/BrandSplash';
import type { FreeMinutes, StaffAlert, StaffApi } from './api';
import { cleanCertificateCode, giftPlan, groupCertificateCode, parseEurosToMinor } from './gift';
import { Certificates } from './Certificates';
import { DayReportView } from './DayReport';
import { PaymentExtras } from './PaymentExtras';
import { ServicePicker } from './ServicePicker';
import { Shop } from './Shop';
import { emptyExtras, evalExtras, type ExtrasDraft, type ExtrasResult } from './payment-extras';
import { addedMinutes, exceedsFreeTime } from './time';
import {
  TABLET_LOCALES,
  TABLET_MESSAGES,
  contentLocale,
  loadTabletLocale,
  saveTabletLocale,
  tabletDay,
  tabletMoney,
  type TabletLocale,
  type TabletMessages,
} from './messages';

/** Therapist tablet — dense and fast between treatments; PT/EN switch on every screen. */
export const Lang = createContext<{ t: TabletMessages; locale: TabletLocale; setLocale: (l: TabletLocale) => void }>({
  t: TABLET_MESSAGES.pt,
  locale: 'pt',
  setLocale: () => undefined,
});
export const useLang = () => useContext(Lang);

export const errText = (t: TabletMessages, e: unknown) =>
  t.errors[e instanceof ApiError ? e.code : 'default'] ?? t.errors.default;

export function LangSwitch() {
  const { locale, setLocale } = useLang();
  return (
    <div className="t-seg t-lang">
      {TABLET_LOCALES.map((l) => (
        <button key={l} className={l === locale ? 'on' : ''} aria-pressed={l === locale} onClick={() => setLocale(l)}>
          {TABLET_MESSAGES[l].langName}
        </button>
      ))}
    </div>
  );
}

export function Tablet({
  staffApi,
  hubApi,
  locationId,
  splash = import.meta.env.MODE !== 'test',
}: {
  staffApi: StaffApi;
  hubApi: HubApi;
  locationId: string;
  splash?: boolean;
}) {
  const [loggedIn, setLoggedIn] = useState(staffApi.loggedIn);
  // Owner 23.10.2026: the brand splash once per session, before the PIN screen.
  const [showSplash, setShowSplash] = useState(() => {
    if (!splash) return false;
    try {
      return sessionStorage.getItem('hub.splash.seen') !== '1';
    } catch {
      return false;
    }
  });
  const [locale, setLocaleState] = useState<TabletLocale>(loadTabletLocale);
  const lang = useMemo(
    () => ({
      t: TABLET_MESSAGES[locale],
      locale,
      setLocale: (l: TabletLocale) => {
        saveTabletLocale(l);
        setLocaleState(l);
      },
    }),
    [locale],
  );
  if (showSplash) {
    return (
      <BrandSplash
        onDone={() => {
          try {
            sessionStorage.setItem('hub.splash.seen', '1');
          } catch {
            // private mode
          }
          setShowSplash(false);
        }}
      />
    );
  }
  return (
    <Lang.Provider value={lang}>
      {loggedIn ? (
        <Day
          staffApi={staffApi}
          hubApi={hubApi}
          locationId={locationId}
          onLogout={() => {
            staffApi.logout();
            setLoggedIn(false);
          }}
        />
      ) : (
        <PinLogin onLogin={(pin) => staffApi.login(locationId, pin).then(() => setLoggedIn(true))} />
      )}
    </Lang.Provider>
  );
}

export function PinLogin({ onLogin }: { onLogin: (pin: string) => Promise<void> }) {
  const { t } = useLang();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const press = (d: string) => setPin((p) => (p.length < 8 ? p + d : p));
  return (
    <div className="tablet pin-screen">
      <LangSwitch />
      <h1>{t.pinTitle}</h1>
      <div className="pin-dots" aria-label="PIN">
        {'•'.repeat(pin.length) || ' '}
      </div>
      {error && <p className="t-error">{error}</p>}
      <div className="pinpad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', 'OK'].map((k) => (
          <button
            key={k}
            disabled={busy}
            className={k === 'OK' ? 'pk ok' : 'pk'}
            onClick={async () => {
              if (k === '⌫') return setPin((p) => p.slice(0, -1));
              if (k !== 'OK') return press(k);
              setBusy(true);
              setError(undefined);
              try {
                await onLogin(pin);
              } catch (e) {
                setError(errText(t, e));
                setPin('');
              } finally {
                setBusy(false);
              }
            }}
          >
            {k === 'OK' ? t.enter : k}
          </button>
        ))}
      </div>
    </div>
  );
}

function Day({
  staffApi,
  hubApi,
  locationId,
  onLogout,
}: {
  staffApi: StaffApi;
  hubApi: HubApi;
  locationId: string;
  onLogout: () => void;
}) {
  const { t, locale } = useLang();
  const [date, setDate] = useState<string>();
  const [todayDate, setTodayDate] = useState<string>();
  const [data, setData] = useState<{ visits: StaffVisit[]; date: string }>();
  const [error, setError] = useState<string>();
  const [openId, setOpenId] = useState<string>();
  const [who, setWho] = useState<string>('all');
  const [staff, setStaff] = useState<Staff[]>([]);
  const [catalog, setCatalog] = useState<Service[]>([]);
  const [alerts, setAlerts] = useState<StaffAlert[]>([]);
  // Owner 15.10.2026: no "Report" / "Certificates" buttons for therapists (certificates are sold at the
  // kiosk). The admin screens stay reachable by address: /tablet#certificates, /tablet#report.
  const [certsOpen, setCertsOpen] = useState(() => window.location.hash === '#certificates');
  const [reportOpen, setReportOpen] = useState(() => window.location.hash === '#report');
  // "Sell a product": shown only when the accounting system is configured (owner 15.10.2026)
  const [shopCfg, setShopCfg] = useState<ShopConfig>();
  // owner 15.10.2026: products are sold inside the visit ("Products for sale"); the standalone
  // screen stays for the administrator at /tablet#shop
  const [shopOpen, setShopOpen] = useState(() => window.location.hash === '#shop');
  useEffect(() => {
    const onHash = () => {
      setCertsOpen(window.location.hash === '#certificates');
      setReportOpen(window.location.hash === '#report');
      setShopOpen(window.location.hash === '#shop');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    // whole team (names for every Altegio booking); kiosk staff list as a fallback
    staffApi.team().then(setStaff, () => hubApi.staff(locationId).then(setStaff, () => undefined));
    hubApi.services(locationId).then(setCatalog, () => undefined);
  }, [staffApi, hubApi, locationId]);

  const load = useCallback(async () => {
    try {
      const r = await staffApi.visits(date);
      setData({ visits: r.data, date: r.date });
      staffApi.alerts().then(setAlerts, () => undefined);
      if (!shopCfg)
        staffApi.shopConfig().then(
          (c) => c.enabled && setShopCfg(c),
          () => undefined,
        );
      if (date === undefined) setTodayDate(r.date);
      setError(undefined);
    } catch (e) {
      if (!staffApi.loggedIn) onLogout();
      setError(errText(t, e));
    }
  }, [staffApi, date, onLogout, t, shopCfg]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(load, 10_000); // kiosk visits and staff calls appear within 10 s
    return () => window.clearInterval(timer);
  }, [load]);

  const staffName = (id: string) => (id === 'desk' ? t.desk : (staff.find((s) => s.id === id)?.displayName ?? '—'));
  const day = data?.visits ?? [];
  const visits = day.filter((v) => who === 'all' || v.staffId === who);
  // filter chips: only therapists who work that day
  const working = [...new Set(day.map((v) => v.staffId))].map((id) => ({ id, displayName: staffName(id) }));
  // Owner 2.11.2026 (board 20c, M2): the day is a strip on top, the chosen visit opens below it.
  // Nothing chosen yet → the visit in progress, else the next one still to come, else the first.
  const open = visits.find((v) => v.id === openId) ?? defaultVisit(visits, data?.date === todayDate);

  if (certsOpen)
    return (
      <Certificates
        api={staffApi}
        t={t}
        locale={locale}
        onBack={() => {
          setCertsOpen(false);
          window.history.replaceState(null, '', window.location.pathname);
          void load();
        }}
      />
    );

  if (shopOpen && shopCfg)
    return (
      <Shop
        api={staffApi}
        config={shopCfg}
        t={t}
        locale={locale}
        onBack={() => {
          setShopOpen(false);
          window.history.replaceState(null, '', window.location.pathname);
        }}
      />
    );

  if (reportOpen)
    return (
      <DayReportView
        api={staffApi}
        date={data?.date}
        staffName={staffName}
        t={t}
        locale={locale}
        onBack={() => {
          setReportOpen(false);
          window.history.replaceState(null, '', window.location.pathname);
        }}
      />
    );

  const plus = (base: string, days: number) => {
    const d = new Date(`${base}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const tomorrow = todayDate ? plus(todayDate, 1) : undefined;
  const shift = (days: number) => setDate(plus(data?.date ?? new Date().toISOString().slice(0, 10), days));

  return (
    <div className="tablet t3">
      <header className="t-bar">
        <span className="t-wordmark" aria-label="360°FACE">
          360<i>°</i>FACE
        </span>
        <div className="t-date">
          <button aria-label={t.prevDay} onClick={() => shift(-1)}>
            ‹
          </button>
          <strong>{data ? tabletDay(data.date, locale) : '…'}</strong>
          <button aria-label={t.nextDay} onClick={() => shift(1)}>
            ›
          </button>
        </div>
        <div className="t-seg">
          <button className={date === undefined ? 'on' : ''} onClick={() => setDate(undefined)}>
            {t.today}
          </button>
          <button
            className={tomorrow !== undefined && date === tomorrow ? 'on' : ''}
            disabled={!tomorrow}
            onClick={() => setDate(tomorrow)}
          >
            {t.tomorrow}
          </button>
        </div>
        <LangSwitch />
        <button className="t-ghost" onClick={onLogout}>
          {t.logout}
        </button>
      </header>
      <div className="t-sub">
        <div className="t-filter">
          {[{ id: 'all', displayName: t.all }, ...working].map((s) => (
            <button key={s.id} className={who === s.id ? 'on' : ''} onClick={() => setWho(s.id)}>
              {s.displayName}
            </button>
          ))}
        </div>
        {data && <span className="t-count">{t.visitsCount(visits.length)}</span>}
        {data && missingReco(visits) > 0 && (
          <span className="t-count warn">{t.recoMissingCount(missingReco(visits))}</span>
        )}
      </div>
      {alerts.map((a) => (
        <div key={a.id} className="t-alert" role="alert">
          <BellIcon />
          <span>
            {a.screen === 'gift_email'
              ? t.alertGiftEmail
              : a.screen === 'video_call'
                ? t.alertVideo
                : a.screen === 'operator'
                  ? t.alertOperator
                  : t.alert(Math.max(0, Math.floor((Date.now() - Date.parse(a.at)) / 60_000)))}
          </span>
          <button
            onClick={() => {
              setAlerts((xs) => xs.filter((x) => x.id !== a.id));
              staffApi.resolveAlert(a.id).catch(() => undefined);
            }}
          >
            {t.alertGo}
          </button>
        </div>
      ))}
      {error && <p className="t-error">{error}</p>}
      {data && visits.length === 0 && <p className="t-empty">{t.empty}</p>}
      {visits.length > 0 && (
        <div className="t-strip" role="tablist" aria-label={t.visitsCount(visits.length)}>
          {visits.map((v) => (
            <button
              key={v.id}
              role="tab"
              aria-selected={v.id === open?.id}
              className={`t-day-tile s-${v.state}${v.id === open?.id ? ' on' : ''}`}
              onClick={() => setOpenId(v.id)}
            >
              <b>{v.start.slice(11, 16)}</b>
              <span>{v.kind === 'sale' ? t.sale : shortName(v.customer.fullName)}</span>
              <small>
                {v.state === 'in_service' || v.state === 'checked_in'
                  ? `● ${t.states[v.state]}`
                  : ['completed', 'closed'].includes(v.state)
                    ? `✓ ${t.states[v.state]}`
                    : v.lines.find((l) => l.ordered > 0)?.title}
              </small>
              {(needsReco(v) || v.questionnaire?.status === 'needed' || v.crmSync?.state === 'failed') && (
                <i className="t-day-tile-dot" aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
      {open && (
        <VisitDetail
          key={open.id}
          inline
          visit={open}
          staffName={staffName(open.staffId)}
          catalog={catalog}
          api={staffApi}
          shopEnabled={Boolean(shopCfg)}
          onBack={() => setOpenId(undefined)}
          onChange={(v) => {
            // a CRM booking gets a Hub id on the first action: replace it by the id that was open
            setData((d) => d && { ...d, visits: d.visits.map((x) => (x.id === open.id ? v : x)) });
            setOpenId(v.id);
          }}
        />
      )}
    </div>
  );
}

/** The visit opened when the therapist has not tapped one: in progress, else the next to come, else the first. */
export function defaultVisit(vs: StaffVisit[], today: boolean, now = new Date()): StaffVisit | undefined {
  const live = vs.find((v) => v.state === 'in_service' || v.state === 'checked_in');
  if (live) return live;
  if (today) {
    const hm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const next = vs.find((v) => v.state === 'booked' && v.start.slice(11, 16) >= hm);
    if (next) return next;
  }
  return vs.find((v) => v.state === 'booked') ?? vs[0];
}

/** Start–end of a visit, «15:00–15:45». */
function timeRange(v: StaffVisit): string {
  const [h, m] = v.start.slice(11, 16).split(':').map(Number);
  const end = h! * 60 + m! + (v.durationMin ?? 0);
  return `${v.start.slice(11, 16)}–${String(Math.floor(end / 60) % 24).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
}

/**
 * Kiosk health form (GDPR art. 9). Availability is checked without reading the answers;
 * the answers are fetched only when the therapist opens them (each read is logged on the
 * server) and are dropped from memory when hidden or when the visit is closed.
 */
/** Owner 19.10.2026: the visit was declined for medical reasons — closed without services, 0 €. */
export function MedicalDeclineNote({ visit }: { visit: StaffVisit }) {
  const { t } = useLang();
  if (!visit.medicalDecline) return null;
  return (
    <div className="t-warn t-form-alert">
      <strong>{t.medicalDeclined}</strong>
      <p>{t.medicalDeclineCrm(visit.medicalDecline.crm)}</p>
    </div>
  );
}

export function HealthForm({
  visitId,
  api,
  onVisitChange,
}: {
  visitId: string;
  api: StaffApi;
  /** After "do not treat" the visit is closed on the server — the screen reloads it. */
  onVisitChange?: (v: StaffVisit) => void;
}) {
  const { t, locale } = useLang();
  const [status, setStatus] = useState<QuestionnaireAvailability>();
  const [form, setForm] = useState<StaffQuestionnaireView | null>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  useEffect(() => {
    let live = true;
    setStatus(undefined);
    setForm(undefined);
    api
      .questionnaireStatus(visitId)
      .then((s) => live && setStatus(s))
      .catch(() => live && setStatus({ available: false }));
    return () => {
      live = false;
    };
  }, [api, visitId]);

  if (!status?.available) return null;
  const open = async () => {
    setBusy(true);
    setError(undefined);
    try {
      setForm(await api.questionnaire(visitId));
    } catch (e) {
      setError(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const decide = async (decision: 'proceed' | 'decline') => {
    setBusy(true);
    setError(undefined);
    try {
      setForm(await api.questionnaireDecision(visitId, decision));
      setConfirmDecline(false);
      if (decision === 'decline' && onVisitChange) onVisitChange(await api.visit(visitId));
    } catch (e) {
      setError(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const legacy = form?.items;
  const yesCount = legacy ? legacy.filter((i) => i.yes).length : 0;
  const declared =
    form && (form.conditions === 'declared' || form.conditions === 'cleared' || form.aesthetic === 'declared');
  return (
    <section className={status.attention ? 't-card t-form attention' : 't-card t-form'}>
      <div className="t-form-head">
        <strong>{t.formSubmitted}</strong>
        <small>
          {status.submittedAt && t.formFilled(tabletDay(status.submittedAt.slice(0, 10), locale))}
          {status.fromEarlierVisit && ` · ${t.formEarlier}`}
          {status.fromIpad && ` · ${t.formFromIpad}`}
        </small>
        <span className="t-spacer" />
        {form ? (
          <button className="t-ghost" onClick={() => setForm(undefined)}>
            {t.formHide}
          </button>
        ) : (
          <button className="t-primary" disabled={busy} onClick={open}>
            {t.formOpen}
          </button>
        )}
      </div>
      {status.attention && !form && <p className="t-warn t-form-alert">{t.formAlert}</p>}
      {error && <p className="t-error">{error}</p>}
      {form === null && <p className="t-info">{t.formNone}</p>}
      {form && legacy && (
        <>
          <p className={yesCount > 0 ? 't-warn t-form-alert' : 't-info'}>
            {yesCount > 0 ? t.formAttention(yesCount) : t.formAllNo}
            {form.draft && <span className="badge">{t.formDraft}</span>}
          </p>
          <p>
            <strong>{form.fullName}</strong>
          </p>
          <ul className="t-answers">
            {legacy.map((i) => (
              <li key={i.questionId} className={i.yes ? 'yes' : ''}>
                <span>{localize(i.text, locale)}</span>
                <strong>{i.yes ? t.formYes : t.formNo}</strong>
                {i.detail && <em>{i.detail}</em>}
              </li>
            ))}
          </ul>
        </>
      )}
      {form && !legacy && (
        <>
          <p>
            <strong>{form.fullName}</strong>
            {form.birthDate && ` · ${t.formBirth} ${form.birthDate.split('-').reverse().join('/')}`}
            {form.draft && <span className="badge">{t.formDraft}</span>}
          </p>
          <p className="t-form-prefs">
            {form.pressure && (
              <span>
                {t.formPressure}: <strong>{form.pressure}/5</strong>
              </span>
            )}
            {form.contact && (
              <span>
                {t.formContact}: <strong>{t.contactLabels[form.contact]}</strong>
              </span>
            )}
            {form.similarBefore !== undefined && (
              <span>
                {t.formSimilar}: <strong>{form.similarBefore ? t.formYes : t.formNo}</strong>
              </span>
            )}
            {form.heardFrom && (
              <span>
                {t.formHeard}: <strong>{t.heardLabels[form.heardFrom]}</strong>
              </span>
            )}
          </p>
          {form.conditions === 'none' && form.aesthetic === 'none' && !form.detail && (
            <p className="t-info">{t.formNoneDeclared}</p>
          )}
          {/* iPad form (owner 18–19.10.2026): only the answer is kept — the therapist asks what */}
          {form.fromIpad && form.conditions === 'declared' && (
            <p className="t-warn t-form-alert">{t.formIpadDeclared}</p>
          )}
          {form.fromIpad && form.conditions === 'cleared' && <p className="t-warn t-form-alert">{t.formIpadCleared}</p>}
          {form.fromIpad && form.aesthetic === 'declared' && (
            <p className="t-warn t-form-alert">{t.formIpadAesthetic}</p>
          )}
          {form.detail && (
            <div className="t-warn t-form-alert t-form-detail">
              {form.detail.conditions.length > 0 || form.detail.conditionNote ? (
                <>
                  <strong>{t.formDeclared}</strong>
                  <ul>
                    {form.detail.conditions.map((c) => (
                      <li key={c.id}>{localize(c.text, locale)}</li>
                    ))}
                    {form.detail.conditionNote && <li>«{form.detail.conditionNote}»</li>}
                  </ul>
                </>
              ) : (
                <p>{t.formNoneDeclared}</p>
              )}
              {form.detail.aestheticDetail && (
                <p>
                  <strong>{t.formAesthetic}</strong> {form.detail.aestheticDetail}
                </p>
              )}
              {form.detail.otherNote && (
                <p>
                  <strong>{t.formOther}</strong> {form.detail.otherNote}
                </p>
              )}
              <small className="t-form-del">{t.formDeletesAt}</small>
            </div>
          )}
          {form.detailDeleted && <p className="t-info">{t.formDetailDeleted}</p>}
          {declared && !form.fromEarlierVisit && (
            <div className="t-decision">
              {form.decision ? (
                <p className={form.decision.value === 'proceed' ? 't-info' : 't-warn'}>
                  {t.formDecided(form.decision.value)}
                </p>
              ) : (
                <>
                  <strong>{t.formDecide}</strong>
                  <button className="t-primary" disabled={busy} onClick={() => void decide('proceed')}>
                    {t.formProceed}
                  </button>
                  <button disabled={busy} onClick={() => setConfirmDecline(true)}>
                    {t.formDecline}
                  </button>
                  {confirmDecline && (
                    <div className="t-warn t-form-alert">
                      <p>{t.formDeclineConfirm}</p>
                      <button className="t-primary" disabled={busy} onClick={() => void decide('decline')}>
                        {t.formDeclineYes}
                      </button>
                      <button disabled={busy} onClick={() => setConfirmDecline(false)}>
                        {t.formDeclineCancel}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {form.signature && (
            <figure className="t-sig">
              <img src={form.signature} alt={t.formSignature} />
              <figcaption>{t.formSignature}</figcaption>
            </figure>
          )}
        </>
      )}
      {form && <p className="t-note">{t.formLogged}</p>}
    </section>
  );
}

function VisitDetail({
  visit,
  staffName,
  catalog,
  api,
  shopEnabled,
  inline = false,
  onBack,
  onChange,
}: {
  visit: StaffVisit;
  staffName: string;
  catalog: Service[];
  api: StaffApi;
  /** Products come from the studio accounting system (owner 15.10.2026). */
  shopEnabled: boolean;
  /** Under the day strip (owner 2.11.2026): no own top bar, a dark client banner instead. */
  inline?: boolean;
  onBack: () => void;
  onChange: (v: StaffVisit) => void;
}) {
  const { t, locale } = useLang();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [picker, setPicker] = useState<'replace' | 'add'>();
  const run = async (f: () => Promise<StaffVisit>) => {
    setBusy(true);
    setError(undefined);
    try {
      onChange(await f());
      setEditing(false);
    } catch (e) {
      setError(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const diffs = lineDiscrepancies(visit.lines);
  const sale = visit.kind === 'sale';
  const c = visit.customer;

  const ordered = visit.lines.filter((l) => l.ordered > 0 || l.performed > 0);
  const art = ordered.map((l) => serviceArt({ pt: l.title })).find(Boolean);
  return (
    <div className={inline ? 't-visit' : 'tablet detail'}>
      {inline ? (
        <section className="t-banner">
          {art && <img className="t-banner-art" src={art} alt="" />}
          <span className="t-banner-fade" aria-hidden />
          <span className="t-banner-text">
            <small>
              {timeRange(visit)} · {staffName} · {t.states[visit.state]}
            </small>
            <b>{sale ? t.sale : c.fullName}</b>
            <span>
              {ordered.map((l) => l.title).join(' + ')} ·{' '}
              {tabletMoney({ amountMinor: billTotalMinor(visit), currency: 'EUR' }, locale)}
            </span>
            <span className="t-banner-pills">
              {visit.source === 'classpass' && <em>ClassPass</em>}
              {visit.bookingStatus === 'from_crm' && <em>Altegio</em>}
              {visit.payment.method && <em>{t.methods[visit.payment.method]}</em>}
              {visit.bookingGift && (
                <em>
                  🎁 {localize(visit.bookingGift.title, locale)}
                  {visit.bookingGift.handedAt && ` · ${t.giftHandedDoneShort}`}
                </em>
              )}
            </span>
          </span>
        </section>
      ) : (
        <header className="t-bar">
          <button className="t-ghost" onClick={onBack}>
            {t.back}
          </button>
          <strong>
            {tabletDay(visit.date, locale)} · {visit.start.slice(11, 16)} · {staffName}
          </strong>
          <span className={`badge b-${visit.state}`}>{t.states[visit.state]}</span>
          <span className="t-spacer" />
          <LangSwitch />
        </header>
      )}

      {sale ? (
        <p className="t-warn">{t.saleInfo}</p>
      ) : visit.bookingStatus === 'requested' ? (
        <p className="t-warn">{t.booking.requested}</p>
      ) : inline ? null : (
        <p className="t-info">{t.booking[visit.bookingStatus]}</p>
      )}
      {visit.crmSync && visit.crmSync.state !== 'skipped' && (
        <p className={visit.crmSync.state === 'ok' ? 't-info' : 't-warn'}>
          {visit.crmSync.state === 'ok' ? t.crmSyncOk : t.crmSyncFailed}
        </p>
      )}
      {visit.questionnaire && (
        <p className={visit.questionnaire.status === 'needed' ? 't-warn' : 't-info'}>
          {{ needed: t.formNeeded, confirmed: t.formOk, submitted: t.formSubmitted }[visit.questionnaire.status]}
        </p>
      )}
      {visit.clientComment && (
        <p className="t-warn">
          💬 {t.clientComment}: «{visit.clientComment}»
        </p>
      )}
      {visit.giftDelivery && (
        <p className={visit.giftDelivery.method === 'email' ? 't-warn' : 't-info'}>
          {visit.giftDelivery.method === 'email' ? `✉ ${t.deliveryEmailNote}` : t.deliveryPickup}
          {visit.giftDelivery.mail && (
            <>
              <br />
              {t.mailStatus[visit.giftDelivery.mail.status]}
            </>
          )}
        </p>
      )}
      {visit.giftDelivery?.card && (
        <p className="t-info">
          {t.cardFor}: <strong>{visit.giftDelivery.card.to}</strong> · {t.cardFrom}:{' '}
          <strong>{visit.giftDelivery.card.from}</strong>
          {visit.giftDelivery.card.message && (
            <>
              <br />«{visit.giftDelivery.card.message}»
            </>
          )}
        </p>
      )}
      {visit.nextVisit && (
        <p className="t-info t-next-line">
          ✓{' '}
          {t.nextBooked(
            `${tabletDay(visit.nextVisit.start.slice(0, 10), locale)} ${visit.nextVisit.start.slice(11, 16)} · ${visit.nextVisit.titles.join(' + ')}`,
          )}
          {' — '}
          {visit.nextVisit.bookingStatus === 'crm_booked' ? t.nextInCrm : t.nextRequested}
        </p>
      )}
      <MedicalDeclineNote visit={visit} />
      {!sale && <HealthForm visitId={visit.id} api={api} onVisitChange={onChange} />}

      <section className={inline ? 't-card t-paycard' : 't-card'}>
        {!inline && <h2>{sale ? t.sale : c.fullName}</h2>}
        {c.nif && <p>NIF {c.nif}</p>}
        {visit.payment.method && !inline && (
          <p>
            {t.preferredPayment}: <strong>{t.methods[visit.payment.method]}</strong>
          </p>
        )}
        {visit.payment.parts && visit.payment.parts.length > 0 && (
          <p>
            {t.paid}:{' '}
            {visit.payment.parts.map((p, i) => (
              <span key={i}>
                {i > 0 && ' + '}
                <strong>{t.methods[p.method]}</strong>{' '}
                {tabletMoney({ amountMinor: p.amountMinor, currency: 'EUR' }, locale)}
                {p.certificateCode && ` (№ ${p.certificateCode})`}
              </span>
            ))}
          </p>
        )}
        {visit.payment.discount && (
          <p className="t-warn">
            {t.discountTitle}: −
            {tabletMoney({ amountMinor: visit.payment.discount.amountMinor, currency: 'EUR' }, locale)} (
            {tabletMoney({ amountMinor: visit.payment.discount.beforeMinor, currency: 'EUR' }, locale)} →{' '}
            {tabletMoney(
              { amountMinor: visit.payment.discount.beforeMinor - visit.payment.discount.amountMinor, currency: 'EUR' },
              locale,
            )}
            ) · {t.reasons[visit.payment.discount.reason]}
            {visit.payment.discount.note && ` — ${visit.payment.discount.note}`}
            {visit.payment.discount.crm && ` · ${t.discountCrm[visit.payment.discount.crm]}`}
          </p>
        )}
        {visit.payment.upgrade && (
          <p className="t-warn">
            {t.adjustUpgrade}:{' '}
            {tabletMoney({ amountMinor: visit.payment.upgrade.beforeMinor, currency: 'EUR' }, locale)} →{' '}
            <strong>{tabletMoney({ amountMinor: visit.payment.upgrade.finalMinor, currency: 'EUR' }, locale)}</strong>
            {visit.payment.upgrade.crm && ` · ${t.discountCrm[visit.payment.upgrade.crm]}`}
          </p>
        )}
        {visit.shopSale && (
          <p className={visit.shopSale.state === 'ok' ? 't-info' : 't-warn'}>
            {visit.shopSale.state === 'ok'
              ? t.shopSaleOk(tabletMoney({ amountMinor: visit.shopSale.totalMinor ?? 0, currency: 'EUR' }, locale))
              : visit.shopSale.state === 'manual'
                ? t.shopSaleManual
                : t.shopSaleFailed}
            {visit.shopSale.detail && <small> — {visit.shopSale.detail}</small>}
            {visit.shopSale.state === 'failed' && (
              <button className="t-ghost" disabled={busy} onClick={() => run(() => api.retryShopSale(visit.id))}>
                {t.shopSaleRetry}
              </button>
            )}
          </p>
        )}
        {visit.payment.tip && (
          <p>
            {t.payTip}:{' '}
            <strong>{tabletMoney({ amountMinor: visit.payment.tip.amountMinor, currency: 'EUR' }, locale)}</strong> (
            {t.methods[visit.payment.tip.method]})
          </p>
        )}
      </section>
      {!sale && <ClientCardBox visitId={visit.id} api={api} />}

      {editing ? (
        <PerformedEditor
          visit={visit}
          catalog={catalog}
          api={api}
          busy={busy}
          onSave={(p, giftHanded) => run(() => api.complete(visit.id, p, giftHanded))}
        />
      ) : (
        <section className="t-card">
          <table className="t-lines">
            <thead>
              <tr>
                <th />
                <th>{t.ordered}</th>
                <th>{t.performed}</th>
                <th>{t.paid}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visit.lines.map((l) => (
                <tr key={l.serviceId}>
                  <td className={l.replacedBy && l.ordered === 0 && l.performed === 0 ? 't-replaced' : ''}>
                    {l.title}
                    {l.replacedBy && l.ordered === 0 && (
                      <small>
                        {' '}
                        → {t.changedTo} {visit.lines.find((x) => x.serviceId === l.replacedBy)?.title ?? l.replacedBy}
                      </small>
                    )}
                    {l.kind === 'product' && <span className="badge kind">{t.kindProduct}</span>}
                    {l.kind === 'gift' && <span className="badge kind">{t.kindGift}</span>}
                    {l.code && <span className="badge">№ {l.code}</span>}
                  </td>
                  <td>{l.ordered}</td>
                  <td>{l.performed}</td>
                  <td>{l.paid}</td>
                  <td>{tabletMoney({ amountMinor: l.unitPriceMinor, currency: 'EUR' }, locale)}</td>
                </tr>
              ))}
              <tr className="tot">
                <td>{t.total}</td>
                <td>{tabletMoney({ amountMinor: totalMinor(visit.lines, 'ordered'), currency: 'EUR' }, locale)}</td>
                <td>{tabletMoney({ amountMinor: totalMinor(visit.lines, 'performed'), currency: 'EUR' }, locale)}</td>
                <td>{tabletMoney({ amountMinor: totalMinor(visit.lines, 'paid'), currency: 'EUR' }, locale)}</td>
                <td />
              </tr>
            </tbody>
          </table>
          <p>
            {t.toPay}: <strong>{tabletMoney({ amountMinor: billTotalMinor(visit), currency: 'EUR' }, locale)}</strong>
          </p>
          {diffs.length > 0 && visit.state !== 'booked' && visit.state !== 'checked_in' && (
            <ul className="t-diffs">
              {diffs.map((d) => (
                <li key={`${d.serviceId}-${d.kind}`}>
                  {visit.lines.find((l) => l.serviceId === d.serviceId)?.title}: {t.diff[d.kind]} ×{d.quantity}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {error && <p className="t-error">{error}</p>}

      <div className="t-actions">
        {visit.state === 'booked' && (
          <>
            <button
              className="t-primary"
              disabled={busy}
              onClick={() => run(() => (sale ? api.setState(visit.id, 'checked_in') : api.arrive(visit.id)))}
            >
              {t.arrived}
            </button>
            <button disabled={busy} onClick={() => run(() => api.setState(visit.id, 'no_show'))}>
              {t.noShow}
            </button>
            <button disabled={busy} onClick={() => run(() => api.setState(visit.id, 'cancelled'))}>
              {t.cancel}
            </button>
          </>
        )}
        {/* Owner 15.10.2026: "client arrived" = treatment started; change it or add extras, then finish */}
        {(visit.state === 'checked_in' || visit.state === 'in_service') && !editing && !picker && !sale && (
          <>
            <button disabled={busy} onClick={() => setPicker('replace')}>
              ⇄ {t.changeTreatment}
            </button>
            <button disabled={busy} onClick={() => setPicker('add')}>
              + {t.addExtras}
            </button>
          </>
        )}
        {(visit.state === 'checked_in' || visit.state === 'in_service') && !editing && !picker && (
          <button className="t-primary" disabled={busy} onClick={() => setEditing(true)}>
            {sale ? t.handOver : t.finish}
          </button>
        )}
        {/* found during the massage (e.g. tight jaw → buccal): added after finishing, before payment */}
        {visit.state === 'completed' && visit.payment.state === 'none' && !sale && !picker && (
          <button disabled={busy} onClick={() => setPicker('add')}>
            + {t.addExtraAfter}
          </button>
        )}
      </div>

      {picker && (
        <ServicePicker
          visit={visit}
          catalog={catalog}
          api={api}
          mode={picker}
          t={t}
          locale={locale}
          onClose={() => setPicker(undefined)}
          onDone={(v) => {
            setPicker(undefined);
            onChange(v);
          }}
        />
      )}

      {visit.state === 'completed' && !sale && visit.payment.state === 'none' && (
        <NextPanel visit={visit} catalog={catalog} api={api} shopEnabled={shopEnabled} onChange={onChange} />
      )}

      {visit.state === 'completed' && (
        <PaymentConfirm
          visit={visit}
          busy={busy}
          api={api}
          onConfirm={(method, pin, nif, certificateCode, extras) =>
            run(() => api.confirmPayment(visit.id, method, pin, nif, certificateCode, extras))
          }
        />
      )}

      {/* owner 15.10.2026: only once the treatment is finished */}
      {!sale && ['completed', 'closed'].includes(visit.state) && (
        <RecommendationBox visit={visit} api={api} staffName={staffName} onChange={onChange} />
      )}
    </div>
  );
}

/** Picture of the free gift (the same 3D pictures as the kiosk extras). */
export function giftArt(id: string): string {
  if (id.includes('hand')) return '/v3/x-hand.jpg';
  if (id.includes('led')) return '/v3/x-led.jpg';
  if (id.includes('mask')) return '/v3/x-mask.jpg';
  return '/v3/gift.png';
}

export function PerformedEditor({
  visit,
  catalog,
  api,
  busy,
  onSave,
}: {
  visit: StaffVisit;
  catalog: Service[];
  api: StaffApi;
  busy: boolean;
  onSave: (performed: Array<{ serviceId: string; quantity: number; code?: string }>, giftHanded: boolean) => void;
}) {
  const { t, locale } = useLang();
  // Owner 02.11.2026: the free gift for the booking amount must be given before finishing.
  const gift = visit.bookingGift && !visit.bookingGift.handedAt ? visit.bookingGift : undefined;
  const [giftHanded, setGiftHanded] = useState(false);
  const initial = useMemo(
    () =>
      Object.fromEntries(
        visit.lines
          // a treatment changed for another one is not offered again
          .filter((l) => !(l.replacedBy && l.ordered === 0 && l.performed === 0))
          .map((l) => [l.serviceId, l.performed || l.ordered]),
      ),
    [visit.lines],
  );
  const [qty, setQty] = useState<Record<string, number>>(initial);
  // Time before the therapist's next booking: a warning only, the therapist decides.
  const [free, setFree] = useState<FreeMinutes>();
  useEffect(() => {
    if (visit.kind === 'sale') return;
    let alive = true;
    api.freeMinutes(visit.id).then(
      (f) => alive && setFree(f),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [api, visit.id, visit.kind]);
  const extra = addedMinutes(visit.lines, qty, catalog);
  const [adding, setAdding] = useState('');
  const [codes, setCodes] = useState<Record<string, string>>(() =>
    Object.fromEntries(visit.lines.filter((l) => l.code).map((l) => [l.serviceId, l.code!])),
  );
  const isGift = (id: string) => visit.lines.find((l) => l.serviceId === id)?.kind === 'gift';
  const title = (id: string) =>
    visit.lines.find((l) => l.serviceId === id)?.title ??
    localize(catalog.find((s) => s.id === id)?.title ?? { pt: id }, contentLocale(locale));

  return (
    <section className="t-card">
      <h3>{t.performedTitle}</h3>
      {Object.entries(qty).map(([id, q]) => (
        <div className="t-step" key={id}>
          <span>{title(id)}</span>
          <button onClick={() => setQty({ ...qty, [id]: Math.max(0, q - 1) })}>−</button>
          <strong>{q}</strong>
          <button onClick={() => setQty({ ...qty, [id]: Math.min(5, q + 1) })}>+</button>
          {isGift(id) && (
            <label className="t-code">
              {t.giftCode}
              {/* A USB/Bluetooth barcode scanner types into the focused field like a keyboard. */}
              <input
                autoFocus
                autoComplete="off"
                inputMode="text"
                value={groupCertificateCode(codes[id] ?? '')}
                onChange={(e) => setCodes({ ...codes, [id]: cleanCertificateCode(e.target.value) })}
              />
            </label>
          )}
        </div>
      ))}
      {visit.kind !== 'sale' && (
        <div className="t-add">
          <select value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">{t.addService}</option>
            {/* owner 15.10.2026: at "Finish" only extras (add-ons); the main treatment is changed before */}
            {catalog
              .filter((s) => s.kind === 'addon' && !(s.id in qty))
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {localize(s.title, contentLocale(locale))} — {tabletMoney(s.price, locale)}
                </option>
              ))}
          </select>
          <button
            disabled={!adding}
            onClick={() => {
              setQty({ ...qty, [adding]: 1 });
              setAdding('');
            }}
          >
            OK
          </button>
        </div>
      )}
      {free && exceedsFreeTime(extra, free.freeMinutes) && (
        <p className="t-warn" role="status">
          {t.noTime(extra, free.freeMinutes ?? 0, free.nextStart?.slice(11, 16) ?? '')}
        </p>
      )}
      {gift && (
        <label className={giftHanded ? 't-gift-check on' : 't-gift-check'}>
          <input type="checkbox" checked={giftHanded} onChange={(e) => setGiftHanded(e.target.checked)} />
          <span>
            <strong>{localize(gift.title, contentLocale(locale))}</strong>
            <em>{gift.takeHome ? t.giftHandedTakeHome : t.giftHandedDone}</em>
          </span>
          <img className="t-gift-check-art" src={giftArt(gift.id)} alt="" />
        </label>
      )}
      {gift && !giftHanded && (
        <p className="t-warn" role="status">
          {t.giftHandedFirst}
        </p>
      )}
      <button
        className="t-primary"
        disabled={busy || Boolean(gift && !giftHanded)}
        onClick={() =>
          onSave(
            Object.entries(qty).map(([serviceId, quantity]) => ({
              serviceId,
              quantity,
              ...(isGift(serviceId) && codes[serviceId] ? { code: codes[serviceId] } : {}),
            })),
            giftHanded,
          )
        }
      >
        {t.save}
      </button>
    </section>
  );
}

/**
 * "On the way out" (owner 15.10.2026): right after the treatment, while the customer is still in
 * the chair — book the next appointment with the same therapist and add home-care products.
 * Both are optional and never block the payment below.
 */
function NextPanel({
  visit,
  catalog,
  api,
  shopEnabled,
  onChange,
}: {
  visit: StaffVisit;
  catalog: Service[];
  api: StaffApi;
  shopEnabled: boolean;
  onChange: (v: StaffVisit) => void;
}) {
  const { t, locale } = useLang();
  const [open, setOpen] = useState<'rebook' | 'products' | null>(null);
  const [shelf, setShelf] = useState<ShopProduct[]>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // Treatment to book again: the visit's own by default (the server picks it when none is sent).
  const [serviceIds, setServiceIds] = useState<string[]>();
  const [dates, setDates] = useState<string[]>();
  const [date, setDate] = useState<string>();
  const [slots, setSlots] = useState<Slot[]>();
  const [slot, setSlot] = useState<string>();
  const [offers, setOffers] = useState<ProductOffer[]>();
  const treatments = catalog.filter((s) => s.kind !== 'addon');

  useEffect(() => {
    if (open !== 'rebook' || visit.nextVisit) return;
    let live = true;
    setDates(undefined);
    setDate(undefined);
    setSlots(undefined);
    setSlot(undefined);
    api.rebookDates(visit.id, serviceIds).then(
      (r) => {
        if (!live) return;
        setDates(r.dates);
        if (!serviceIds) setServiceIds(r.serviceIds);
      },
      (e) => live && setError(errText(t, e)),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, open, visit.id, visit.nextVisit, serviceIds?.join(',')]);

  useEffect(() => {
    if (!date) return;
    let live = true;
    setSlots(undefined);
    setSlot(undefined);
    api.rebookSlots(visit.id, date, serviceIds).then(
      (r) => live && setSlots(r),
      (e) => live && setError(errText(t, e)),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, visit.id, date]);

  useEffect(() => {
    if (open !== 'products' || !shopEnabled || shelf) return;
    let live = true;
    api.shopProducts().then(
      (r) => live && setShelf(r),
      (e) => live && setError(errText(t, e)),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, open, shopEnabled]);

  useEffect(() => {
    if (open !== 'products' || offers || shopEnabled) return;
    let live = true;
    api.products().then(
      (r) => live && setOffers(r),
      (e) => live && setError(errText(t, e)),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, open]);

  const run = async (f: () => Promise<StaffVisit>) => {
    setBusy(true);
    setError(undefined);
    try {
      onChange(await f());
    } catch (e) {
      setError(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const inBill = (id: string) => visit.lines.find((l) => l.serviceId === id)?.performed ?? 0;
  const when = (start: string) => `${tabletDay(start.slice(0, 10), locale)} ${start.slice(11, 16)}`;

  return (
    <section className="t-card t-next">
      <h3>{t.nextTitle}</h3>
      <div className="t-tiles">
        <button
          className={`t-tile${open === 'rebook' ? ' on' : ''}${visit.nextVisit ? ' done' : ''}`}
          onClick={() => setOpen(open === 'rebook' ? null : 'rebook')}
        >
          <strong>
            {visit.nextVisit ? '✓ ' : ''}
            {t.nextRebook}
          </strong>
          <small>{visit.nextVisit ? when(visit.nextVisit.start) : t.nextRebookSub}</small>
        </button>
        <button
          className={`t-tile${open === 'products' ? ' on' : ''}`}
          onClick={() => setOpen(open === 'products' ? null : 'products')}
        >
          <strong>{shopEnabled ? t.nextShopProducts : t.nextProducts}</strong>
          <small>{shopEnabled ? t.nextShopProductsSub : t.nextProductsSub}</small>
        </button>
      </div>
      {error && <p className="t-error">{error}</p>}

      {open === 'rebook' && !visit.nextVisit && (
        <div className="t-rebook">
          <label className="t-field">
            {t.nextTreatment}
            <select
              value={serviceIds?.[0] ?? ''}
              onChange={(e) => setServiceIds(e.target.value ? [e.target.value] : undefined)}
            >
              {treatments.map((s) => (
                <option key={s.id} value={s.id}>
                  {localize(s.title, contentLocale(locale))} — {tabletMoney(s.price, locale)}
                </option>
              ))}
            </select>
          </label>
          {dates && dates.length === 0 && <p className="t-warn">{t.nextNoDates}</p>}
          {dates && dates.length > 0 && (
            <>
              <p className="t-note">{t.nextPickTime}</p>
              <div className="t-chips">
                {dates.map((d) => (
                  <button key={d} className={`t-chip${d === date ? ' on' : ''}`} onClick={() => setDate(d)}>
                    {tabletDay(d, locale)}
                  </button>
                ))}
              </div>
            </>
          )}
          {date && slots && (
            <div className="t-chips">
              {slots.length === 0 && <p className="t-warn">{t.nextNoDates}</p>}
              {slots.map((s) => (
                <button
                  key={s.start}
                  className={`t-chip${s.start === slot ? ' on' : ''}`}
                  onClick={() => setSlot(s.start)}
                >
                  {s.start.slice(11, 16)}
                </button>
              ))}
            </div>
          )}
          {slot && (
            <button
              className="t-primary"
              disabled={busy}
              onClick={() => run(() => api.rebook(visit.id, slot, serviceIds))}
            >
              {t.nextConfirm(when(slot))}
            </button>
          )}
        </div>
      )}

      {open === 'products' && shopEnabled && (
        <ul className="t-offers t-shelf">
          {shelf?.map((p) => {
            const qty = inBill(`uchet-${p.id}`);
            return (
              <li key={p.id}>
                {p.thumbUrl || p.photoUrl ? (
                  <img src={p.thumbUrl ?? p.photoUrl} alt="" />
                ) : (
                  <span className="t-shelf-ph" />
                )}
                <span>
                  <strong>{[p.brand, p.name].filter(Boolean).join(' · ')}</strong>
                  <small>
                    {tabletMoney({ amountMinor: p.priceMinor, currency: p.currency }, locale)}
                    {p.volume && ` · ${p.volume}`} · {t.shopInStock(p.inStock)}
                  </small>
                </span>
                <div className="t-step">
                  <button
                    disabled={busy || qty === 0}
                    onClick={() => run(() => api.setShopProducts(visit.id, [{ productId: p.id, qty: qty - 1 }]))}
                  >
                    −
                  </button>
                  <strong>{qty}</strong>
                  <button
                    disabled={busy || qty >= p.inStock}
                    onClick={() => run(() => api.setShopProducts(visit.id, [{ productId: p.id, qty: qty + 1 }]))}
                  >
                    +
                  </button>
                </div>
              </li>
            );
          })}
          {shelf && shelf.length === 0 && <li className="t-note">{t.nextShopEmpty}</li>}
        </ul>
      )}

      {open === 'products' && !shopEnabled && (
        <ul className="t-offers">
          {offers?.map((p) => (
            <li key={p.id}>
              <span>
                <strong>{localize(p.title, contentLocale(locale))}</strong>
                <small>
                  {tabletMoney(p.finalPrice, locale)}
                  {p.promotion && ` · −${p.promotion.percentOff}%`}
                  {inBill(p.id) > 0 && ` · ${t.nextAdded} ×${inBill(p.id)}`}
                </small>
              </span>
              <button disabled={busy} onClick={() => run(() => api.addProducts(visit.id, [p.id]))}>
                + {t.nextAdd}
              </button>
            </li>
          ))}
          {offers && offers.length === 0 && <li className="t-note">—</li>}
        </ul>
      )}
    </section>
  );
}

/** Completed treatments without a recommendation yet (to fill in at the end of the shift). */
export function needsReco(v: StaffVisit): boolean {
  return v.kind !== 'sale' && (v.state === 'completed' || v.state === 'closed') && !v.recommendation;
}
function missingReco(vs: StaffVisit[]): number {
  return vs.filter(needsReco).length;
}

/**
 * Client card (owner 15.10.2026): visits, last treatments, notes, pressure — no phone/e-mail.
 * Since 20.10.2026: the CRM card Note, the whole comment history behind "show all", and an internal
 * note for the next therapists (appended to the CRM Note).
 */
export function ClientCardBox({ visitId, api }: { visitId: string; api: StaffApi }) {
  const { t, locale } = useLang();
  const [card, setCard] = useState<ClientCard | null>();
  const [all, setAll] = useState(false);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    setCard(undefined);
    setAll(false);
    setAdding(false);
    setText('');
    api.clientCard(visitId).then(
      (c) => live && setCard(c),
      () => live && setCard(null),
    );
    return () => {
      live = false;
    };
  }, [api, visitId]);
  if (!card) return null;
  const month = (d: string) => `${d.slice(5, 7)}/${d.slice(0, 4)}`;
  const shown = all ? card.notes : card.notes.slice(0, CARD_LAST_VISITS);
  const save = async () => {
    if (text.trim().length < 3 || busy) return;
    setBusy(true);
    setError(false);
    try {
      setCard(await api.addClientNote(visitId, text.trim()));
      setText('');
      setAdding(false);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="t-card t-client">
      <p className="t-client-head">
        {(card.regular || card.totalVisits === 0) && (
          <span className={card.regular ? 'badge regular' : 'badge first'}>
            {card.regular ? `★ ${t.cardRegular}` : `✦ ${t.cardNew}`}
          </span>
        )}
        {card.totalVisits === 0 && card.history === 'crm' && <span className="t-first-hint">{t.cardNewHint}</span>}
        {card.totalVisits > 0 && (
          <span>{t.cardVisits(card.totalVisits, card.totalIsMinimum, card.firstVisit && month(card.firstVisit))}</span>
        )}
        {card.prefs?.pressure && (
          <span>
            {t.cardPressure}: <strong>{card.prefs.pressure}/5</strong>
          </span>
        )}
      </p>
      {card.history === 'unavailable' && <p className="t-note">{t.cardNoHistory}</p>}
      {card.clientNote && (
        <>
          <h3>{t.cardClientNote}</h3>
          <p className="t-client-note">{card.clientNote}</p>
        </>
      )}
      {card.lastVisits.length > 0 && (
        <>
          <h3>{t.cardLast}</h3>
          <ul className="t-history">
            {card.lastVisits.map((v, i) => (
              <li key={i}>
                <span>{tabletDay(v.date, locale)}</span>
                <strong>{v.services.join(' + ')}</strong>
                {v.products.length > 0 && <small>🛍 {v.products.join(', ')}</small>}
                {v.staffName && <small>{v.staffName}</small>}
              </li>
            ))}
          </ul>
        </>
      )}
      {card.notes.length > 0 && (
        <>
          <h3>{t.cardNotes}</h3>
          <ul className="t-notes">
            {shown.map((n, i) => (
              <li key={i}>
                <small>
                  {tabletDay(n.date, locale)}
                  {n.staffName && ` · ${n.staffName}`}
                </small>
                <span>{n.text}</span>
              </li>
            ))}
          </ul>
          {card.notes.length > CARD_LAST_VISITS && (
            <button type="button" className="t-more" onClick={() => setAll((x) => !x)}>
              {all ? t.cardShowLess : t.cardShowAll(card.notes.length)}
            </button>
          )}
        </>
      )}
      {card.canAddNote &&
        (adding ? (
          <div className="t-add-note">
            <textarea
              value={text}
              maxLength={CLIENT_NOTE_TEXT_MAX}
              rows={3}
              autoFocus
              onChange={(e) => setText(e.target.value)}
            />
            <small>{t.cardAddNoteHint}</small>
            {error && <p className="t-error">{t.cardAddNoteError}</p>}
            <div className="t-add-note-actions">
              <button type="button" className="secondary" onClick={() => setAdding(false)} disabled={busy}>
                {t.cardAddNoteCancel}
              </button>
              <button type="button" onClick={save} disabled={busy || text.trim().length < 3}>
                {t.cardAddNoteSave}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="t-add-note-open" onClick={() => setAdding(true)}>
            {t.cardAddNote}
          </button>
        ))}
    </section>
  );
}

/**
 * One protocol section as a dropdown (owner 2.11.2026: «выпадающие меню, чтобы сократить страницу»).
 * Single sections replace the pick; multi sections add it and show the picks as removable pills.
 */
function PickRow({
  label,
  options,
  picked,
  multi,
  placeholder,
  bare = false,
  onChange,
}: {
  label: string;
  options: Array<{ id: string; label: string }>;
  picked: string[];
  multi: boolean;
  placeholder: string;
  /** No label above: the dropdown's empty option names the section. */
  bare?: boolean;
  onChange: (ids: string[]) => void;
}) {
  const name = (id: string) => options.find((o) => o.id === id)?.label ?? id;
  const free = options.filter((o) => !picked.includes(o.id));
  return (
    <div className="t-pick">
      {!bare && <span className="t-proto-label">{label}</span>}
      <select
        aria-label={label}
        value={multi ? '' : (picked[0] ?? '')}
        onChange={(e) => {
          const id = e.target.value;
          if (!id) return multi ? undefined : onChange([]);
          onChange(multi ? [...picked, id] : [id]);
        }}
      >
        <option value="">{multi && picked.length ? `+ ${placeholder}` : placeholder}</option>
        {(multi ? free : options).map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      {multi && picked.length > 0 && (
        <div className="t-pick-pills">
          {picked.map((id) => (
            <button
              key={id}
              type="button"
              className="t-pick-pill"
              aria-label={`${name(id)} ×`}
              onClick={() => onChange(picked.filter((x) => x !== id))}
            >
              {name(id)} <i>×</i>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const SEC = (id: string) => PROTOCOL_SECTIONS.find((x) => x.id === id)!;

/** A protocol block (owner 2.11.2026, board 21 P1): a card with a 3D picture on the left. */
function ProtoCard({
  img,
  title,
  client = false,
  children,
}: {
  img: string;
  title: string;
  client?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={client ? 't-proto-card client' : 't-proto-card'}>
      <span className="t-proto-art">
        <img src={img} alt="" />
      </span>
      <div className="t-proto-body">
        <span className="t-proto-label">{title}</span>
        {children}
      </div>
    </section>
  );
}

/** "Recommendations for the client": written by the therapist, e-mailed the next day at 11:00. */
export function RecommendationBox({
  visit,
  api,
  staffName = '',
  onChange,
}: {
  visit: StaffVisit;
  api: StaffApi;
  staffName?: string;
  onChange: (v: StaffVisit) => void;
}) {
  const { t, locale } = useLang();
  const r = visit.recommendation;
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(r?.text ?? '');
  const [productIds, setProductIds] = useState<string[]>(r?.products.map((p) => p.id) ?? []);
  const [weeks, setWeeks] = useState<number | undefined>(r?.nextInWeeks);
  const [send, setSend] = useState(r ? r.send.status !== 'not_sent' : true);
  // treatment protocol (owner 2.11.2026): picked from the paper form's lists
  const [picked, setPicked] = useState<string[]>(r?.protocol?.picked ?? []);
  const [free, setFree] = useState({
    faceMask: r?.protocol?.faceMask ?? '',
    ampoule: r?.protocol?.ampoule ?? '',
    other: r?.protocol?.other ?? '',
    treatmentType: r?.protocol?.treatmentType ?? '',
  });
  const clientPicked = PROTOCOL_SECTIONS.filter((x) => x.client).some((x) =>
    x.options.some((o) => picked.includes(o.id)),
  );
  const canSave = text.trim().length >= 3 || productIds.length > 0 || clientPicked;
  const ordered = visit.lines.filter((l) => l.ordered > 0 || l.performed > 0);
  const art = ordered.map((l) => serviceArt({ pt: l.title })).find(Boolean);
  const pickRow = (sec: (typeof PROTOCOL_SECTIONS)[number], bare = false) => (
    <PickRow
      key={sec.id}
      label={sec.title[locale]}
      bare={bare}
      options={sec.options.map((o) => ({ id: o.id, label: o.label[locale] }))}
      picked={picked.filter((id) => sec.options.some((o) => o.id === id))}
      multi={sec.multi}
      placeholder={sec.title[locale]}
      onChange={(ids) => setPicked((xs) => [...xs.filter((id) => !sec.options.some((o) => o.id === id)), ...ids])}
    />
  );
  const [offers, setOffers] = useState<ProductOffer[]>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (!open || offers) return;
    api.products().then(setOffers, () => setOffers([]));
  }, [api, open, offers]);
  const sent = r?.send.status === 'sent';

  if (!open) {
    return (
      <section className={r ? 't-card t-reco' : 't-card t-reco empty'}>
        {r ? (
          <>
            <h3>{t.recoTitle}</h3>
            {r.text && <p className="t-reco-text">{r.text}</p>}
            {protocolForClient(r.protocol, locale).map((x) => (
              <p key={x.section}>
                <strong>{x.title}:</strong> {x.items.join(', ')}
              </p>
            ))}
            {r.products.length > 0 && <p>🛍 {r.products.map((p) => p.title).join(', ')}</p>}
            {r.nextInWeeks && (
              <p>
                {t.recoWeeks} {t.recoWeeksN(r.nextInWeeks)}
              </p>
            )}
            <p className={r.send.status === 'failed' ? 't-warn' : 't-info'}>{t.recoStatus[r.send.status]}</p>
            {!sent && (
              <button className="t-ghost" onClick={() => setOpen(true)}>
                {t.recoEdit}
              </button>
            )}
          </>
        ) : (
          <button className="t-primary t-reco-btn" onClick={() => setOpen(true)}>
            {t.protoButton}
          </button>
        )}
      </section>
    );
  }

  return (
    <div className="t-sheet" role="dialog" aria-label={t.protoTitle}>
      <header className="t-sheet-bar">
        <button type="button" className="t-ghost" onClick={() => setOpen(false)} disabled={busy}>
          {t.cardAddNoteCancel}
        </button>
        <strong>{t.protoTitle}</strong>
        <span className="t-sheet-who">{visit.customer.fullName}</span>
      </header>
      <div className="t-sheet-body">
        <section className="t-banner t-banner-sm">
          {art && <img className="t-banner-art" src={art} alt="" />}
          <span className="t-banner-fade" aria-hidden />
          <span className="t-banner-text">
            <small>
              {timeRange(visit)} · {staffName} · {t.states[visit.state]}
            </small>
            <b>{visit.customer.fullName}</b>
            <span>{ordered.map((l) => l.title).join(' + ')}</span>
          </span>
        </section>
        <h4 className="t-proto-h">{t.protoStudio}</h4>
        <ProtoCard
          img="/v3/s-cleansing.jpg"
          title={[SEC('cleansing'), SEC('tonic'), SEC('massage_cream')].map((x) => x.title[locale]).join(' · ')}
        >
          <div className="t-proto-row3">
            {['cleansing', 'tonic', 'massage_cream'].map((id) => pickRow(SEC(id), true))}
          </div>
        </ProtoCard>
        <ProtoCard
          img="/v3/x-mask.jpg"
          title={`${SEC('finishing').title[locale]} · ${SEC('extra_used').title[locale]}`}
        >
          {pickRow(SEC('finishing'))}
          {pickRow(SEC('extra_used'))}
          <div className="t-proto-free">
            {(['faceMask', 'ampoule', 'treatmentType', 'other'] as const).map((k) => {
              const name = {
                faceMask: t.protoMask,
                ampoule: t.protoAmpoule,
                treatmentType: t.protoType,
                other: t.protoOther,
              }[k];
              return (
                <input
                  key={k}
                  aria-label={name}
                  placeholder={name}
                  value={free[k]}
                  maxLength={k === 'other' ? 200 : 80}
                  onChange={(e) => setFree((f) => ({ ...f, [k]: e.target.value }))}
                />
              );
            })}
          </div>
        </ProtoCard>
        <h4 className="t-proto-h client">{t.protoClient}</h4>
        <ProtoCard img="/v3/x-eyes.jpg" title={SEC('attention').title[locale]} client>
          {pickRow(SEC('attention'))}
        </ProtoCard>
        <ProtoCard img="/v3/s-buccal.jpg" title={SEC('services').title[locale]} client>
          {pickRow(SEC('services'))}
        </ProtoCard>
        <ProtoCard img="/v3/x-head.jpg" title={`${t.recoWeeks} · ${t.protoOwn}`} client>
          <div className="t-chips">
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                className={`t-chip${weeks === n ? ' on' : ''}`}
                onClick={() => setWeeks(weeks === n ? undefined : n)}
              >
                {t.recoWeeksN(n)}
              </button>
            ))}
          </div>
          <textarea
            rows={3}
            maxLength={1000}
            placeholder={t.protoOwn}
            aria-label={t.protoOwn}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {offers && offers.length > 0 && (
            <>
              <span className="t-proto-label">{t.recoProducts}</span>
              <div className="t-chips">
                {offers.map((p) => {
                  const on = productIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`t-chip${on ? ' on' : ''}`}
                      onClick={() =>
                        setProductIds(on ? productIds.filter((x) => x !== p.id) : [...productIds, p.id].slice(0, 5))
                      }
                    >
                      {localize(p.title, contentLocale(locale))}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {visit.customer.hasEmail ? (
            <button
              type="button"
              className="t-check"
              role="checkbox"
              aria-checked={send}
              onClick={() => setSend(!send)}
            >
              <span className="box">{send ? '✓' : ''}</span>
              {t.recoSend}
            </button>
          ) : (
            <p className="t-note">{t.recoNoEmail}</p>
          )}
        </ProtoCard>
        {error && <p className="t-error">{error}</p>}
      </div>
      <footer className="t-sheet-foot">
        {!canSave && <span className="t-sheet-need">{t.protoNeed}</span>}
        <button
          type="button"
          className="t-primary"
          disabled={busy || !canSave}
          onClick={async () => {
            setBusy(true);
            setError(undefined);
            try {
              const extra = Object.fromEntries(
                Object.entries(free)
                  .map(([k, v]) => [k, v.trim()])
                  .filter(([, v]) => v),
              );
              onChange(
                await api.saveRecommendation(visit.id, {
                  text: text.trim(),
                  productIds,
                  nextInWeeks: weeks,
                  send,
                  protocol: { picked, ...extra },
                }),
              );
              setOpen(false);
            } catch (e) {
              setError(errText(t, e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {t.recoSave}
        </button>
      </footer>
    </div>
  );
}

export function PaymentConfirm({
  visit,
  busy,
  onConfirm,
  api,
}: {
  visit: StaffVisit;
  busy: boolean;
  onConfirm: (
    method: PaymentMethod,
    pin: string,
    nif: string | undefined,
    certificateCode: string | undefined,
    extras: ExtrasResult['extras'],
  ) => void;
  api: StaffApi;
}) {
  const { t, locale } = useLang();
  const [method, setMethod] = useState<PaymentMethod>(visit.payment.method ?? 'card_terminal');
  const [pin, setPin] = useState('');
  const [nif, setNif] = useState(visit.customer.nif ?? '');
  // Gift card: number (typed or scanned), looked-up certificate, method for the rest.
  const [code, setCode] = useState('');
  const [cert, setCert] = useState<GiftCertificateView | 'not_found'>();
  const [rest, setRest] = useState<RemainderMethod>();
  const [regValue, setRegValue] = useState('');
  const [giftBusy, setGiftBusy] = useState(false);
  const [giftError, setGiftError] = useState<string>();
  const bill = billTotalMinor(visit);
  const [draft, setDraft] = useState<ExtrasDraft>(() => emptyExtras(visit.payment.method ?? 'card_terminal'));
  const ev = evalExtras(draft, bill);
  const gift = method === 'gift_card';
  // the certificate covers the bill after the discount
  const plan = gift && cert && cert !== 'not_found' ? giftPlan(ev.dueMinor, cert) : undefined;
  const money = (amountMinor: number) =>
    tabletMoney({ amountMinor, currency: cert && cert !== 'not_found' ? cert.currency : 'EUR' }, locale);
  const regMinor = parseEurosToMinor(regValue);

  const giftCall = async (f: () => Promise<GiftCertificateView>) => {
    setGiftBusy(true);
    setGiftError(undefined);
    try {
      setCert(await f());
    } catch (e) {
      if (e instanceof ApiError && e.code === 'GIFT_NOT_FOUND') setCert('not_found');
      else setGiftError(errText(t, e));
    } finally {
      setGiftBusy(false);
    }
  };
  const lookup = () => code.length >= 3 && giftCall(() => api.giftCertificate(code));

  const giftReady =
    ev.dueMinor === 0 || plan?.kind === 'covers_all' || (plan?.kind === 'partial' && rest !== undefined);
  const confirm = () => {
    if (ev.problem) return;
    if (!gift) return onConfirm(method, pin, nif || undefined, undefined, ev.extras);
    if (ev.dueMinor === 0) return onConfirm('cash', pin, nif || undefined, undefined, ev.extras);
    if (!cert || cert === 'not_found') return;
    onConfirm(plan?.kind === 'partial' ? rest! : 'gift_card', pin, nif || undefined, cert.code, ev.extras);
  };

  return (
    <section className="t-card pay">
      <h3>
        {t.payTitle}: {tabletMoney({ amountMinor: bill, currency: 'EUR' }, locale)}
      </h3>
      <p className="t-hint">{t.payHint}</p>
      <div className="t-filter">
        {PAYMENT_METHODS.map((m) => (
          <button key={m} className={m === method ? 'on' : ''} aria-pressed={m === method} onClick={() => setMethod(m)}>
            {t.methods[m]}
          </button>
        ))}
      </div>
      {gift && (
        <div className="t-gift">
          <label className="t-pin">
            {t.giftCode}
            {/* A USB/Bluetooth barcode scanner types into the focused field and ends with Enter. */}
            <input
              autoFocus
              autoComplete="off"
              inputMode="text"
              value={groupCertificateCode(code)}
              onChange={(e) => {
                setCode(cleanCertificateCode(e.target.value));
                setCert(undefined);
                setRest(undefined);
                setGiftError(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void lookup();
                }
              }}
            />
          </label>
          <button disabled={giftBusy || code.length < 3} onClick={() => void lookup()}>
            {t.giftLookup}
          </button>
          {cert && cert !== 'not_found' && (
            <p className="t-info">{t.giftValue(money(cert.valueMinor), money(cert.balanceMinor))}</p>
          )}
          {cert && cert !== 'not_found' && cert.expiresOn && plan?.kind !== 'not_sold' && (
            <p className="t-hint">{t.giftUntil(cert.expiresOn.split('-').reverse().join('/'))}</p>
          )}
          {plan?.kind === 'not_sold' && (
            <p className="t-warn" role="status">
              {t.giftStock}
            </p>
          )}
          {plan?.kind === 'used_up' && (
            <p className="t-warn" role="status">
              {t.giftUsedUp}
            </p>
          )}
          {plan?.kind === 'covers_all' && (
            <p className="t-info" role="status">
              {t.giftCoversAll(money(plan.leftMinor))}
            </p>
          )}
          {plan?.kind === 'partial' && (
            <>
              <p className="t-warn" role="status">
                {t.giftPartial(money(plan.coversMinor), money(plan.missingMinor))}
              </p>
              <div className="t-filter">
                {REMAINDER_METHODS.map((m) => (
                  <button
                    key={m}
                    className={m === rest ? 'on' : ''}
                    aria-pressed={m === rest}
                    onClick={() => setRest(m)}
                  >
                    {t.methods[m]}
                  </button>
                ))}
              </div>
            </>
          )}
          {cert === 'not_found' && (
            <div className="t-gift-register">
              <p className="t-warn" role="status">
                {t.giftNotFound}
              </p>
              <p className="t-hint">{t.giftRegisterHint}</p>
              <label className="t-pin">
                {t.giftRegisterValue}
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  value={regValue}
                  onChange={(e) => setRegValue(e.target.value.replace(/[^\d.,]/g, '').slice(0, 8))}
                />
              </label>
              <button
                disabled={giftBusy || !regMinor || pin.length < 4}
                onClick={() => void giftCall(() => api.registerGiftCertificate(code, regMinor!, pin))}
              >
                {t.giftRegister}
              </button>
            </div>
          )}
          {giftError && <p className="t-error">{giftError}</p>}
        </div>
      )}
      {/* a certificate is sold at its face value: tip only */}
      <PaymentExtras
        draft={draft}
        onChange={setDraft}
        result={ev}
        billMinor={bill}
        t={t}
        money={money}
        tipOnly={visit.kind === 'sale'}
      />
      <label className="t-pin">
        {t.nif}
        <input
          inputMode="numeric"
          autoComplete="off"
          value={nif}
          onChange={(e) => setNif(e.target.value.replace(/\D/g, '').slice(0, 9))}
        />
      </label>
      <label className="t-pin">
        {t.pin}
        <input
          inputMode="numeric"
          type="password"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
        />
      </label>
      <button
        className="t-primary"
        disabled={
          busy || pin.length < 4 || (nif.length > 0 && nif.length !== 9) || (gift && !giftReady) || Boolean(ev.problem)
        }
        onClick={confirm}
      >
        {t.confirmPay}
      </button>
    </section>
  );
}

export function BellIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  );
}
